import { PageRef } from 'providers/page-ref';

/** What the user wants done with their training program. */
export enum WorkoutsUpdateChoice {
  /** build new workouts from the new settings, losing any exercise the user swapped in */
  Rebuild = 'rebuild',
  /** keep the exercises, work out the set volumes again */
  VolumesOnly = 'volumesOnly',
  /** leave the training program exactly as it is */
  Keep = 'keep',
}

/** What the user wants done with their daily calorie target. */
export enum NutritionUpdateChoice {
  /** work the daily calorie target out again from the new settings */
  Update = 'update',
  /**
   * Keep the daily calorie TOTAL the plan already has.
   *
   * This never means "touch nothing about nutrition": a change to how many meals
   * a day the user eats still repartitions that total across the new number of
   * meals, or the plan would hold targets for a meal count that no longer exists.
   */
  Keep = 'keep',
}

export type UpdatePlanOptionsPageParams = {
  returnCallback?: string;
  local?: {
    /** true when the user's exercise customizations would be lost by a rebuild */
    hasTrainingPlanCustomizations: boolean;
    /**
     * Whether to ask the nutrition question at all.
     *
     * False in coach mode, where every document readable from the intake form belongs to
     * the coach rather than to the client being edited, so there is no honest answer to
     * show — and the answer would change nothing there anyway.
     */
    askAboutNutrition: boolean;
    /** true when the plan's daily calorie target is a number the user typed in themselves */
    hasAcceptedTarget: boolean;
    /**
     * The user's own daily calorie target in kcal — the same figure the app shows on the
     * nutrition card and on the Edit Daily Nutrition Goal screen, which is what makes every
     * screen name the same number.
     *
     * It is whichever of the two shapes applies (see `readUsersOwnCalorieTarget` in
     * `@cf-shared/plan-update-shared`): a number the user typed that the solver has not
     * applied yet, or the live target once it has. A not-yet-applied number has NOT been
     * through the solver's minimum-intake floor, so it can sit below it. Only set when
     * hasAcceptedTarget is true.
     */
    acceptedTarget_kcal?: number;
    /**
     * What the solver would work the daily target out to instead, in kcal. Absent both when there
     * is no accepted target to replace and when the re-solve behind the figure could not be
     * completed — in the second case the warning drops the figure rather than naming one nobody
     * computed (df-f3070b96b839).
     */
    recommendedTarget_kcal?: number;
    /**
     * Whether answering "update" would actually change the daily target — true when
     * something that feeds it differs from what is applied. Without this the screen names a
     * replacement number for a user who changed only, say, how many meals they eat, where
     * the target is not recalculated at all.
     */
    targetWouldChange: boolean;
    /** the user moved the energy-balance (deficit / surplus) slider in this edit */
    energyBalanceChanged: boolean;
    /** the user flipped between fat loss and muscle growth in this edit */
    goalDirectionChanged: boolean;
    /** the user changed their body weight or body fat in this edit */
    bodyDataChanged: boolean;
    /** the user changed how active they are outside training in this edit */
    activityLevelChanged: boolean;
    returnContext?: unknown;
  };
};

export interface UpdatePlanOptionsPageResult {
  workouts: WorkoutsUpdateChoice;
  nutrition: NutritionUpdateChoice;
}

export const IUpdatePlanOptionsPage: PageRef<UpdatePlanOptionsPageParams, UpdatePlanOptionsPageResult | undefined> = {
  pageName: 'UpdatePlanOptionsPage',
} as PageRef<UpdatePlanOptionsPageParams, UpdatePlanOptionsPageResult | undefined>;
